// Checkout service-subtotal math — the ONE place that decides how a service ticket is priced, so a
// combined "A + B" appointment can never double-charge. (Jose 2026-10-21)
//
// THE BUG THIS FIXES: a multi-service ("A + B") appointment was detected by `price === 0`. When a price
// was present — a manual override at checkout, OR an imported combined appt that stored the TOTAL in
// `price` — the detection flipped to single-service and the per-service line items got billed AGAIN on
// top of the price → a $55 ticket rang up $110/$115. Detection is now by NAME; an override REPLACES the
// service subtotal (never adds); the line items are the service for a combined ticket (never re-billed).

export interface ServiceSubtotalInput {
  /** The appointment's service name — a combined ticket contains " + " (e.g. "Eyebrow Waxing + Short Classic"). */
  serviceName: string;
  /** The appointment's OWN stored base price (appointment.price ?? 0) — NOT the manual override. */
  origBasePrice: number;
  /** A manual price the operator typed to REPLACE the service subtotal (up or down), else null. */
  priceOverride: number | null;
  /** Sum of the per-service line items (additionalItems of type 'service'). */
  serviceItemsTotal: number;
  /** How many per-service line items exist. */
  serviceItemCount: number;
}

export interface ServiceSubtotalResult {
  isMultiService: boolean;
  /** What the service line charges (the override wins and REPLACES; never added on top). */
  displayServicePrice: number;
  /** Line items billed SEPARATELY — only for a single-primary ticket; 0 for a combined ticket. */
  extraServicesBillable: number;
}

export function computeServiceSubtotal(i: ServiceSubtotalInput): ServiceSubtotalResult {
  const isMultiService = (i.serviceName || '').includes(' + ');
  // The natural (pre-override) service subtotal: the line-item sum for a combined ticket (falling back to
  // the base price if a combined ticket somehow has no itemised breakdown), else the single base price.
  const naturalServiceSubtotal = isMultiService
    ? (i.serviceItemCount >= 1 ? i.serviceItemsTotal : i.origBasePrice)
    : i.origBasePrice;
  // A manual override REPLACES the whole service subtotal — it is never added on top of the line items.
  const displayServicePrice = i.priceOverride != null ? i.priceOverride : naturalServiceSubtotal;
  // Combined ticket → the line items ARE the service (already in displayServicePrice); never re-bill them.
  // Single-primary ticket → the line items are genuine extras that bill on top of the primary.
  const extraServicesBillable = isMultiService ? 0 : i.serviceItemsTotal;
  return { isMultiService, displayServicePrice, extraServicesBillable };
}
